import { CSSProperties, useMemo, useRef } from "react";
import nearExpirationIcon from "@/assets/bell-on-other.svg";
import onTimeIcon from "@/assets/clock-one-solid-full.svg";
import outTimeIcon from "@/assets/octagon-exclamation-solid-full.svg";
import { useTranslation } from "react-i18next";
import { useClaimTimelineKPIs } from "../../hooks/useClaimTimelineKPIs";
import { TimelineStatusEnum } from "../../types/enums";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addFilter, saveBeforeFilter } from "../../../../redux/filtersSlice";
import { ClaimTimelineKpiItem } from "../../types";
import "./ClaimTimelineKPIs.css"
import { Card } from "react-bootstrap";
import ChartShareActions from "../../../../components/ChartShareActions";
import { RootState } from "../../../../redux/store";
import { AuditActionEnum } from "../../../auditLogs/types";
import { generateCsvFile } from "../../../../utils/downloadCsvFile";

export function ClaimTimelineKPIs() {
    const { t } = useTranslation()    
    const { data } = useClaimTimelineKPIs();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const timelineChartRef = useRef<HTMLDivElement>(null);
    const selectedFilters = useSelector((state: RootState) => state.filters.claims.selectedFilters);

    const calculatePercentage = (count: number, total: number) =>
        total > 0 ? Math.round((count / total) * 100) : 0;
    
    const items: ClaimTimelineKpiItem[] = useMemo(() => [
        {
            label: t("dashboard:timelineKPIs.outTime"),
            count: data?.claimsOutTimeCount || 0,
            percentage: calculatePercentage(data?.claimsOutTimeCount || 0, data?.totalClaims || 0),
            icon: (
                <img 
                    title={t("dashboard:timelineKPIs.outTime")}
                    src={outTimeIcon}
                    alt={t("dashboard:timelineKPIs.outTime")}
                    width={30}
                    height={30}
                />
            ),
            timelineStatus: TimelineStatusEnum.Out
        },
        {
            label: t("dashboard:timelineKPIs.nearExpiration"),
            count: data?.claimsNearExpirationCount || 0,
            percentage: calculatePercentage(data?.claimsNearExpirationCount || 0, data?.totalClaims || 0),
            icon: (
                <img 
                    title={t("dashboard:timelineKPIs.nearExpiration")}
                    src={nearExpirationIcon}
                    alt={t("dashboard:timelineKPIs.nearExpiration")}
                    width={30}
                    height={30}
                />
            ),
            timelineStatus: TimelineStatusEnum.Warning
        },
        {
            label: t("dashboard:timelineKPIs.onTime"),
            count: data?.claimsOnTimeCount || 0,
            percentage: calculatePercentage(data?.claimsOnTimeCount || 0, data?.totalClaims || 0),
            icon: (
                <img 
                    title={t("dashboard:timelineKPIs.onTime")}
                    src={onTimeIcon}
                    alt={t("dashboard:timelineKPIs.onTime")}
                    width={30}
                    height={30}
                />
            ),
            timelineStatus: TimelineStatusEnum.On
        },
        {
            label: t("dashboard:timelineKPIs.withoutTimeline"),
            count: data?.claimsWithoutTimelineCount || 0,
            percentage: calculatePercentage(data?.claimsWithoutTimelineCount || 0, data?.totalClaims || 0),
            icon: (
                <i className="bi bi-dash-lg" 
                    title={t("dashboard:timelineKPIs.withoutTimeline")}
                    style={{ fontSize: "x-large", color: "grey" }}
                ></i>
            ),
            timelineStatus: TimelineStatusEnum.WithoutTimeline
        },
    ], [data]);

    const exportToCSV = async (): Promise<void> => {
        const rows = (items ?? []) as ClaimTimelineKpiItem[];
        await generateCsvFile({
            headers: [
                t("dashboard:timelineKPIs.status"),
                t("dashboard:timelineKPIs.number"),
                t("dashboard:timelineKPIs.percentage"),
            ],
            rows,
            mapRow: (item) => [
                t(`claim:timelineStatus.${TimelineStatusEnum[item.timelineStatus]}`),
                item.count ?? 0,
                item.percentage ?? 0,
            ],
            fileBaseName: "claims_status_timeline",
            trailingSemicolon: true,
            audit: {
                entity: "DashboardIndicator",
                action: AuditActionEnum.ExportCsv,
                source: window.location.pathname,
                description: "CSV export of claim status timeline",
                filters: selectedFilters
            },
            
        });
    };
    
    const getBorderStyle = (timelineStatus: TimelineStatusEnum): CSSProperties => {
        switch(timelineStatus) {
            case TimelineStatusEnum.On:
                return { borderLeft: "10px solid #92ca9a" }
            case TimelineStatusEnum.Out:
                return { borderLeft: "10px solid #ef7b78" }
            case TimelineStatusEnum.Warning:
                return { borderLeft: "10px solid rgb(255, 200, 118)" }
            default:
                return { borderLeft: "10px solid rgb(204, 204, 204)" }
        }
    }

    return (
        <Card className="p-3 card-kpi-container">
            <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                <h4 className="mb-0">{t("claim:timelineKPIs.title")}</h4>
                <ChartShareActions
                    chartRef={timelineChartRef}
                    title={t("claim:timelineKPIs.title")}
                    filters={selectedFilters}
                    t={t}
                    exportCsvAction={exportToCSV}
                />
            </div>
            <div className="timeline-kpi-container" ref={timelineChartRef}>
                {items.map((item, i) => (
                    <button
                        key={i}
                        className="timeline-kpi-item-container"
                        onClick={() =>{                        
                            const filter = {
                                name: 'Timeline',
                                values: [item.timelineStatus.toString()]
                            };
                            dispatch(saveBeforeFilter('claims'));
                            dispatch(addFilter({ table: 'claims', filter: filter }));
                            navigate('/claims/historic');
                        }}
                        style={getBorderStyle(item.timelineStatus)}
                    >
                        {item.icon}
                        <span className="timeline-text">
                            {item.label} :{" "} {item.count} - {item.percentage}%
                        </span>
                    </button>
                ))}
            </div>
        </Card>
    );
}